/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { getMultiCdnUrl } from '../utils/imageUtils';
import { CDN_URLS, OCI_FOLDERS } from '../config';

interface LookbookItem {
    id: string;
    title: string;
    imageUrl: string;
    category: string;
}

const lookbookItems: LookbookItem[] = [
    {
        id: 'minimalist-capsule',
        title: 'Minimalist Essentials',
        imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.LOOKBOOKS}virtual-lookbook_7.png`,
        category: 'Capsule'
    },
    {
        id: 'business-capsule',
        title: 'Executive Capsule',
        imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.LOOKBOOKS}virtual-lookbook_6.png`,
        category: 'Business'
    },
    {
        id: 'seasonal-capsule',
        title: 'Seasonal Transition',
        imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.LOOKBOOKS}virtual-lookbook_5.png`,
        category: 'Seasonal'
    },
    {
        id: 'travel-capsule',
        title: 'Jetsetter Wardrobe',
        imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.LOOKBOOKS}virtual-lookbook_4.png`,
        category: 'Travel'
    },
    {
        id: 'weekend-capsule',
        title: 'Relaxed Weekend',
        imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.LOOKBOOKS}virtual-lookbook_3.png`,
        category: 'Casual'
    }
];

const LookbookCarousel: React.FC = () => {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = 400;
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <section className="py-16 bg-white dark:bg-gray-900 overflow-hidden relative group/carousel">
            <div className="max-w-6xl mx-auto px-4">
                <div className="mb-10">
                    <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">
                        {t('capsuleGenerator_lookbook_title')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        {t('capsuleGenerator_lookbook_subtitle')}
                    </p>
                </div>

                <div className="relative">
                    {/* Arrows */}
                    <button
                        onClick={() => scroll('left')}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-white dark:hover:bg-gray-700 -ml-4"
                        aria-label="Previous Lookbook"
                    >
                        <ChevronLeftIcon className="w-6 h-6 text-gray-900 dark:text-white" />
                    </button>

                    <div
                        ref={scrollRef}
                        className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 no-scrollbar"
                    >
                        {lookbookItems.map((item) => (
                            <motion.div
                                key={item.id}
                                whileHover={{ y: -10 }}
                                className="min-w-[280px] w-[280px] h-[380px] relative rounded-2xl overflow-hidden shadow-lg flex-shrink-0 cursor-pointer"
                            >
                                <img
                                    src={getMultiCdnUrl(item.imageUrl, 'BLOG')}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 bg-white/20 backdrop-blur-md border border-white/30 rounded-full text-[10px] font-bold text-white uppercase tracking-wider">
                                        {item.category}
                                    </span>
                                </div>
                                <div className="absolute bottom-6 left-6 right-6">
                                    <h3 className="text-xl font-serif font-bold text-white leading-tight">
                                        {item.title}
                                    </h3>
                                    <div className="mt-3 w-8 h-0.5 bg-indigo-500" />
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <button
                        onClick={() => scroll('right')}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-white dark:hover:bg-gray-700 -mr-4"
                        aria-label="Next Lookbook"
                    >
                        <ChevronRightIcon className="w-6 h-6 text-gray-900 dark:text-white" />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default LookbookCarousel;
